import { useTranslation } from "react-i18next";

/**
 * The API sends `category` as an untranslated literal code ("New" | "Improved" | "Fixed") —
 * unlike the entry descriptions, which arrive already resolved to the user's language. This
 * component is the only place that turns the code into a label. Rendering the raw value would
 * show English category labels in every locale.
 */
const CATEGORY_KEYS: Record<string, string> = {
    new: 'patchNotes.category.new',
    improved: 'patchNotes.category.improved',
    fixed: 'patchNotes.category.fixed',
};

export function categoryModifier(category: string): string {
    const key = category?.toLowerCase();
    return key in CATEGORY_KEYS ? `pn-badge-${key}` : 'pn-badge-unknown';
}

/** Colour-only class for the bullet dot — deliberately not `.pn-badge`, which carries padding. */
export function dotModifier(category: string): string {
    const key = category?.toLowerCase();
    return key in CATEGORY_KEYS ? `pn-dot-${key}` : 'pn-dot-unknown';
}

interface CategoryBadgeProps {
    category: string,
}

export default function CategoryBadge({ category }: CategoryBadgeProps) {
    const { t } = useTranslation();

    const key = category?.toLowerCase();
    // An unrecognised code (someone added a fourth category to patch-notes.json without
    // updating the app) falls back to showing the raw code rather than an empty badge.
    const label = key in CATEGORY_KEYS ? t(CATEGORY_KEYS[key]) : category;

    return (
        <span className={`pn-badge ${categoryModifier(category)}`}>{label}</span>
    );
}
